// @ts-nocheck

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { cn } from "@/lib/utils/cn";
import { useToast } from "@/components/ui/ToastProvider";
import { IconLoader } from "@/components/ui/Icons";

const editSchema = z.object({
  title: z.string().min(5).max(300),
  content: z.string().max(40000).optional(),
  is_nsfw: z.boolean(),
  is_spoiler: z.boolean(),
});
type EditForm = z.infer<typeof editSchema>;

interface EditPostFormProps {
  post: { id: string; title: string; content: string | null; type: string; is_nsfw: boolean; is_spoiler: boolean; is_oc: boolean };
  topics: Array<{ id: string; name: string; slug: string; color: string }>;
}

export function EditPostForm({ post }: EditPostFormProps) {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  const [submitting, setSubmitting] = useState(false);

  const { register, handleSubmit, watch, formState: { errors } } = useForm<EditForm>({
    resolver: zodResolver(editSchema),
    defaultValues: { title: post.title, content: post.content || "", is_nsfw: post.is_nsfw, is_spoiler: post.is_spoiler },
  });

  const title = watch("title", "");
  const content = watch("content", "");

  const onSubmit = async (data: EditForm) => {
    setSubmitting(true);
    try {
      const res = await fetch(`/api/posts/${post.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: data.title, content: data.content, is_nsfw: data.is_nsfw, is_spoiler: data.is_spoiler }),
      });
      if (!res.ok) throw new Error((await res.json()).error?.message || "Failed");
      const { post: updated } = await res.json();
      success("Post updated");
      navigate(`/post/${updated.id}/${updated.slug}`);
      // navigate.refresh() - not needed in Vite;
    } catch (err: unknown) {
      showError("Update failed", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-1.5">
        <div className="flex justify-between items-center">
          <label className="text-sm font-medium text-foreground">Title</label>
          <span className={cn("text-xs tabular-nums", title.length > 280 ? "text-destructive" : "text-text-subtle")}>{title.length}/300</span>
        </div>
        <input {...register("title")} className={cn("input-lg w-full", errors.title && "input-error")} />
        {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
      </div>

      {post.type === "text" && (
        <div className="space-y-1.5">
          <div className="flex justify-between items-center">
            <label className="text-sm font-medium text-foreground">Content</label>
            <span className={cn("text-xs tabular-nums", (content?.length || 0) > 38000 ? "text-destructive" : "text-text-subtle")}>{content?.length || 0}/40,000</span>
          </div>
          <textarea {...register("content")} rows={8} className="input w-full resize-y py-3 min-h-[200px]" />
        </div>
      )}

      <div className="flex gap-4">
        {(["is_nsfw", "is_spoiler"] as const).map(field => (
          <label key={field} className="flex items-center gap-2 cursor-pointer group">
            <input type="checkbox" {...register(field)} className="sr-only" />
            <div className={cn("w-4 h-4 rounded border-2 flex items-center justify-center transition-all", watch(field) ? "bg-brand border-brand" : "border-input")}>
              {watch(field) && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><polyline points="20 6 9 17 4 12" /></svg>}
            </div>
            <span className="text-sm text-text-muted group-hover:text-foreground transition-colors capitalize">{field.replace("is_", "")}</span>
          </label>
        ))}
      </div>

      <div className="flex gap-3 pt-2">
        <button type="button" onClick={() => navigate(-1)} className="btn-secondary btn-lg">Cancel</button>
        <button type="submit" disabled={submitting} className="btn-primary btn-lg flex-1">
          {submitting ? <><IconLoader className="w-4 h-4" />Saving...</> : "Save changes"}
        </button>
      </div>
    </form>
  );
}
